/**
 * Soft≠Live Forge grounding — Live seats / ports / board-ask decide the draft.
 * Soft RECIPES may supply clarify copy only after Live can carry the needs.
 */
import { SOFT_ROLE_TO_SERVER } from "./board-seat-adapter.js";
import { PIN_ROLES } from "./edge-data.js";

const SERVER_TO_SOFT = Object.fromEntries(
  Object.entries(SOFT_ROLE_TO_SERVER).map(([soft, srv]) => [srv, soft]),
);

/** Live binding role → Soft recipe role id (pir/door/…). */
export function softRoleOfLive(role) {
  const r = String(role || "").trim().toLowerCase();
  if (!r) return "";
  if (SOFT_ROLE_TO_SERVER[r]) return r;
  return SERVER_TO_SOFT[r] || r;
}

export function liveBoundSeats(liveSeatItems) {
  const out = [];
  for (const s of liveSeatItems || []) {
    if (!s || typeof s !== "object") continue;
    const binding = s.binding && typeof s.binding === "object" ? s.binding : null;
    const role = softRoleOfLive(s.role || s.assigned_role || (binding && binding.role));
    if (!role) continue;
    const st = String(
      s.state || s.status || (binding && binding.state) || "",
    ).toLowerCase();
    out.push({
      seat_id: String(s.seat_id || s.id || "").trim(),
      role,
      state: st,
      gpio: s.gpio,
    });
  }
  return out;
}

/** Live GET /ports — port is live when API says so (never Soft PORTS.state). */
export function livePortIds(portsPayload) {
  const items =
    (portsPayload && (portsPayload.items || portsPayload.ports)) || [];
  const ids = new Set();
  for (const p of items) {
    if (!p || typeof p !== "object") continue;
    const id = String(p.port_id || p.id || "").trim();
    if (!id) continue;
    const live = p.live;
    const state = String(p.state || p.status || "").toLowerCase();
    const ok =
      live === true ||
      (live && typeof live === "object" && (live.connected || live.up || live.state === "live")) ||
      state === "live" ||
      state === "up" ||
      state === "connected";
    if (ok) ids.add(id);
  }
  return ids;
}

function askAnswer(askPayload) {
  if (!askPayload) return "";
  const root = askPayload.data || askPayload;
  return String(root.answer || root.reply || root.say || root.text || "").trim();
}

function askMeta(askPayload) {
  if (!askPayload) return { facts: [], fresh: false, by: "" };
  const root = askPayload.data || askPayload;
  return {
    facts: Array.isArray(root.facts_used) ? root.facts_used : [],
    fresh: !!root.telemetry_fresh,
    by: String(root.by || root.mode || ""),
  };
}

/**
 * Resolve a compose draft from Live seats + optional Soft recipe clarify copy.
 * Soft brochure alone never wins — Live must carry every `need`.
 */
export function resolveLiveForgeDraft({ text, recipes, liveSeatItems, livePortSet, askPayload }) {
  const q = String(text || "").trim();
  const seats = liveBoundSeats(liveSeatItems);
  const roleSet = new Set(seats.map((s) => s.role));
  const ports = livePortSet instanceof Set ? livePortSet : new Set(livePortSet || []);
  const answer = askAnswer(askPayload);
  const meta = askMeta(askPayload);
  const askOk = !!(askPayload && askPayload.ok !== false && (answer || meta.by));

  const softHit = (recipes || []).find((r) => r && r.match && r.match.test(q));

  const needCarried = (need) => {
    if (!need) return true;
    if (need.role) return roleSet.has(need.role);
    if (need.port) return ports.has(need.port);
    return false;
  };

  if (softHit) {
    const miss = [];
    for (const n of softHit.need || []) {
      if (needCarried(n)) continue;
      miss.push({
        what: n.role ? ("role " + n.role) : ("port " + n.port),
        why: "Live board does not report this binding yet",
        fix: "Bind / confirm on Board · Live, then Re-probe",
        role: n.role || null,
        port: n.port || null,
      });
    }
    const liveNeed = (softHit.need || [])
      .filter(needCarried)
      .map((n) => {
        if (n.role) {
          const seat = seats.find((s) => s.role === n.role);
          return {
            role: n.role,
            as: n.as,
            seat_id: seat ? seat.seat_id : null,
            state: seat ? seat.state : "",
          };
        }
        return { ...n, live: ports.has(n.port) };
      });
    const liveWant = (softHit.want || []).map((n) => {
      if (n.role) {
        const seat = seats.find((s) => s.role === n.role);
        return {
          role: n.role,
          as: n.as,
          seat_id: seat ? seat.seat_id : null,
          state: seat ? seat.state : "",
          missing: !seat,
        };
      }
      return { ...n, live: ports.has(n.port), missing: n.port ? !ports.has(n.port) : false };
    });

    if (miss.length === 0) {
      const caps = [
        ...new Set(
          liveNeed
            .map((n) => (PIN_ROLES.find((r) => r.id === n.role) || {}).cap)
            .filter(Boolean),
        ),
      ];
      return {
        ...softHit,
        source: "live",
        need: liveNeed,
        want: liveWant,
        miss: (softHit.miss || []).filter((m) => !m.role || roleSet.has(m.role)),
        caps: caps.length ? caps : softHit.caps || ["snapshot"],
        say:
          (answer
            ? ("Live Board Ask: " + answer.slice(0, 280) + (answer.length > 280 ? "…" : "") + " ")
            : "") + softHit.say,
        hi: [
          ...(meta.facts || []),
          ...liveNeed.map((n) => n.seat_id || n.role || n.port).filter(Boolean),
        ].slice(0, 6),
        askMeta: meta,
      };
    }

    /* Soft keywords matched but Live cannot carry — still a Live-honest partial. */
    return {
      ...softHit,
      source: "live_partial",
      need: liveNeed,
      want: liveWant,
      miss,
      say:
        "Live board cannot carry every required seat yet. Soft brochure wiring is not used as truth. "
        + (answer ? ("Board Ask: " + answer.slice(0, 200)) : "Re-probe or bind missing seats on Board."),
      hi: ["live_partial", ...miss.map((m) => m.what)],
      askMeta: meta,
    };
  }

  /* No Soft recipe keywords — upgrade path: Live inventory draft when Ask + seats exist. */
  if (askOk && seats.length > 0) {
    const need = seats.slice(0, 6).map((s) => ({
      role: s.role,
      as: s.seat_id || s.role,
      seat_id: s.seat_id,
      state: s.state,
    }));
    const caps = [
      ...new Set(
        need
          .map((n) => (PIN_ROLES.find((r) => r.id === n.role) || {}).cap)
          .filter(Boolean),
      ),
    ];
    return {
      id: "live_board_draft",
      source: "live",
      title: "Live board draft",
      match: /./,
      need,
      want: [],
      miss: [],
      caps: caps.length ? caps : ["snapshot"],
      rule: {
        when: "operator intent against Live seats",
        and: [
          meta.fresh ? "telemetry fresh" : "telemetry stale or absent",
          "facts: " + (meta.facts.join(", ") || "pins"),
        ],
        then: ["compose pin_bindings from Live seats", "capability registry claim on Dispatch"],
      },
      say:
        answer
        || ("Live reports " + seats.length + " bound seats. Clarify intent, then attest and Compose."),
      hi: need.map((n) => n.seat_id || n.role).filter(Boolean).slice(0, 5),
      askMeta: meta,
    };
  }

  return null;
}

/** Operator policy questions — HITL intent, not Soft brochure seat invent. */
export const POLICY_ASK_DEFAULT = [
  {
    id: "cost",
    kind: "policy",
    q: "What is the cost of being wrong?",
    why: "Sets how sure compose must be before it acts. Soft defaults are forbidden — you decide.",
    opts: [
      { id: "low", t: "A wasted trip", say: "Fires readily. You will get some noise." },
      { id: "high", t: "Someone is woken at 3 a.m.", say: "Holds until it is sure. It will miss some real ones." },
    ],
  },
  {
    id: "offline",
    kind: "policy",
    q: "Should it still work with the uplink down?",
    why: "On-device survives outage but sees only this node. Cloud correlates the site but stops when the link does.",
    opts: [
      { id: "device", t: "Keep working offline", say: "Runs on the node. Cannot see other devices." },
      { id: "cloud", t: "Let it see the whole site", say: "Runs upstream. Stops when the link stops." },
    ],
  },
];

/**
 * Soft≠Live Clarify: Live seat keep/drop + execute confirm first;
 * Soft recipe.ask / policy questions only refine intent after seats.
 */
export function buildLiveClarifyQuestions(rec) {
  const qs = [];
  const needs = (rec && Array.isArray(rec.need) ? rec.need : []).filter(Boolean);
  for (const n of needs) {
    const sid = String(n.seat_id || "").trim();
    const role = String(n.role || "").trim();
    if (!sid && !role) continue;
    const key = sid || role;
    qs.push({
      id: "seat_" + key,
      kind: "live_seat",
      seat_id: sid || null,
      role: role || null,
      q: "Keep Live seat " + (sid || role) + (role && sid ? (" · " + role) : "") + " in compose?",
      why: "This seat came from Live GET /devices/{id}/pins. Drop omits it from pin_bindings — Soft geometry is not consulted.",
      opts: [
        { id: "keep", t: "Keep - Live", say: "Included in forge/compose pin_bindings." },
        { id: "drop", t: "Drop for now", say: "Omitted from this compose. Bind later on Board." },
      ],
    });
    const meta = PIN_ROLES.find((r) => r.id === role);
    if (meta && (meta.cls === "execute" || meta.risk === "high")) {
      qs.push({
        id: "exec_" + key,
        kind: "live_exec",
        seat_id: sid || null,
        role: role || null,
        q: "Confirm execute-class " + (sid || role) + " before arming?",
        why: "Live compose can set confirm on the seat. Soft auto-arm theatre is deleted.",
        opts: [
          { id: "confirm", t: "Confirm on compose", say: "pin_bindings.confirm = true for this seat.", risk: true },
          { id: "hold", t: "Bind without confirm flag", say: "Seat is written; confirm flag stays false." },
        ],
      });
    }
  }
  const policy =
    rec && Array.isArray(rec.ask) && rec.ask.length
      ? rec.ask.map((q) => ({ ...q, kind: q.kind || "policy" }))
      : POLICY_ASK_DEFAULT;
  return [...qs, ...policy];
}

/** Build Live pin_bindings from draft needs + clarify answers (Soft pins only for seat_id fallback). */
export function composePinBindingsFromClarify({ rec, ans, pins, seatIdOfPin, toServerRole }) {
  const needs = (rec && Array.isArray(rec.need) ? rec.need : []).filter(Boolean);
  const bindings = [];
  for (const n of needs) {
    const sid = String(n.seat_id || "").trim();
    const role = String(n.role || "").trim();
    const key = sid || role;
    if (!key) continue;
    if (ans && ans["seat_" + key] === "drop") continue;
    let seat_id = sid;
    if (!seat_id && pins && seatIdOfPin) {
      const soft = pins.find((p) => p.role === role);
      seat_id = soft ? seatIdOfPin(soft) : "";
    }
    if (!seat_id) continue;
    const serverRole = toServerRole ? toServerRole(role) : role;
    if (!serverRole) continue;
    const meta = PIN_ROLES.find((r) => r.id === role);
    const execAns = ans && ans["exec_" + key];
    const confirm =
      execAns === "confirm" ||
      (!execAns && !!(meta && (meta.cls === "execute" || meta.risk === "high")));
    const soft = (pins || []).find((p) => seatIdOfPin && seatIdOfPin(p) === seat_id);
    bindings.push({
      seat_id,
      role: serverRole,
      confirm: !!confirm,
      function_label: (soft && soft.fn) || n.as || "",
    });
  }
  return bindings;
}

export function clarifyRevisionNote(rec, ans) {
  const bits = [String((rec && rec.id) || "forge"), "Live clarify"];
  if (ans && ans.cost) bits.push("cost=" + ans.cost);
  if (ans && ans.offline) bits.push("offline=" + ans.offline);
  const kept = Object.keys(ans || {}).filter((k) => k.startsWith("seat_") && ans[k] === "keep");
  const dropped = Object.keys(ans || {}).filter((k) => k.startsWith("seat_") && ans[k] === "drop");
  if (kept.length) bits.push("keep=" + kept.length);
  if (dropped.length) bits.push("drop=" + dropped.length);
  return bits.join(" · ");
}

/** Soft≠Live Plan/Code: exact forge/compose body from Live seats + Clarify — Soft C theatre deleted. */
export function buildLiveComposePlan({ rec, ans, device, pins, seatIdOfPin, toServerRole }) {
  const pin_bindings = composePinBindingsFromClarify({
    rec,
    ans,
    pins,
    seatIdOfPin,
    toServerRole,
  });
  const note = clarifyRevisionNote(rec, ans);
  const claimed = (rec && rec.caps) || ["snapshot"];
  const tested = pin_bindings
    .filter((b) => b.confirm)
    .map((b) => {
      const soft = (pins || []).find((p) => seatIdOfPin && seatIdOfPin(p) === b.seat_id);
      const role = soft && soft.role;
      const cap = role ? (PIN_ROLES.find((r) => r.id === role) || {}).cap : null;
      return cap || null;
    })
    .filter(Boolean);
  const exec = pin_bindings.some((b) => b.confirm);
  return {
    soft: false,
    honesty: "Plan is the Live POST /devices/{id}/forge/compose body. Soft C fragment theatre is deleted.",
    device_id: String((device && device.id) || "").trim(),
    device_label: String((device && (device.label || device.display_label)) || "").trim(),
    class: exec ? "execute" : "read",
    source: (rec && rec.source) || "live",
    body: {
      operator_reason:
        "CMC forge compose · " + ((rec && (rec.title || rec.id)) || "capability") + " · " + note,
      revision_note: note,
      pin_bindings,
      claimed,
      device_type: "edge_ai_node",
      tested,
    },
  };
}

export function formatLiveManifest(plan) {
  const b = (plan && plan.body) || {};
  const binds = Array.isArray(b.pin_bindings) ? b.pin_bindings : [];
  const lines = [
    "soft: false",
    "honesty: Live forge/compose plan (not Soft brochure)",
    "capability: " + String((b.revision_note || "").split(" · ")[0] || "forge"),
    "target: " + String((plan && plan.device_id) || "—"),
    "label: " + String((plan && plan.device_label) || "—"),
    "class: " + String((plan && plan.class) || "read"),
    "revision_note: " + String(b.revision_note || "—"),
    "binds:",
  ];
  if (!binds.length) lines.push("  - (none kept after Clarify)");
  for (const p of binds) {
    lines.push(
      "  - seat " + p.seat_id + " role/" + p.role
        + (p.confirm ? " confirm=true" : "")
        + (p.function_label ? (" # " + p.function_label) : ""),
    );
  }
  lines.push("claimed: " + JSON.stringify(b.claimed || []));
  lines.push("tested: " + JSON.stringify(b.tested || []));
  lines.push("write: POST /devices/{id}/forge/compose");
  return lines.join("\n");
}

export function formatLiveComposePayload(plan) {
  const body = (plan && plan.body) || {};
  try {
    return JSON.stringify(body, null, 2);
  } catch {
    return String(body);
  }
}

/** Live rule view from kept seats + policy answers — Soft recipe prose is optional footnote only. */
export function buildLiveRuleView(rec, ans, plan) {
  const binds = (plan && plan.body && plan.body.pin_bindings) || [];
  const when =
    binds.length
      ? ("Live seats " + binds.map((b) => b.seat_id + "/" + b.role).join(", "))
      : "no Live seats kept after Clarify";
  const and = [];
  if (ans && ans.cost) and.push("operator cost=" + ans.cost);
  if (ans && ans.offline) and.push("operator offline=" + ans.offline);
  if (plan && plan.class === "execute") and.push("execute-class confirm flags on Live seats");
  and.push("compose stays locked until HITL checklist");
  const then = [
    "POST forge/compose with " + binds.length + " pin_bindings",
    "claim " + JSON.stringify((plan && plan.body && plan.body.claimed) || []),
  ];
  const softFoot =
    rec && rec.rule && rec.source !== "live_board_draft"
      ? String(rec.rule.when || "")
      : "";
  return { when, and, then, softFoot };
}
